import { useState, useRef, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import ExtensionSelectionModal from './ExtensionSelectionModal';

export default function Header() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isAffiliateOpen, setIsAffiliateOpen] = useState(false);
	const [isPricingOpen, setIsPricingOpen] = useState(false);
  const [isExtensionModalOpen, setIsExtensionModalOpen] = useState(false);
  const location = useLocation();

  const pricingRef = useRef<HTMLDivElement>(null);
  const affiliateRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (pricingRef.current && !pricingRef.current.contains(event.target as Node)) {
        setIsPricingOpen(false);
      }
      if (affiliateRef.current && !affiliateRef.current.contains(event.target as Node)) {
        setIsAffiliateOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const navigationBefore = [
    { name: 'Home', href: '/' },
    { name: 'How it Works', href: '/how-it-works' },
  ];

  const navigationAfter = [
    { name: 'About', href: '/about' },
    { name: 'Contact', href: '/contact' },
  ];

  const pricingLinks = [
    { name: 'Free & Monthly Plans', href: '/pricing' },
    { name: 'Sample Invoices', href: '/custom-invoice-design' },
  ];

  const affiliateLinks = [
    { name: 'Accountants', href: '/affiliates/accountants' },
    { name: 'Consultants', href: '/affiliates/consultants' },
    { name: 'Partners', href: '/affiliates/partners' },
  ];

  const isAffiliateActive = affiliateLinks.some(link => location.pathname === link.href);
	const isPricingActive = pricingLinks.some(link => location.pathname === link.href);

  const openExtensionModal = () => {
    setIsExtensionModalOpen(true);
  };

  const closeExtensionModal = () => {
    setIsExtensionModalOpen(false);
  };

  const renderNavLink = (item: { name: string; href: string }) => (
    <Link
      key={item.name}
      to={item.href}
      className="text-sm font-semibold leading-6 transition-colors"
      style={{ color: location.pathname === item.href ? '#29B3E6' : '#111827' }}
      onMouseEnter={(e) => (e.currentTarget.style.color = '#29B3E6')}
      onMouseLeave={(e) => {
        if (location.pathname !== item.href) {
          e.currentTarget.style.color = '#111827';
        }
      }}
    >
      {item.name}
    </Link>
  );

  return (
    <>
      <header className="bg-white shadow-sm sticky top-0 z-50">
        <nav className="mx-auto flex max-w-7xl items-center justify-between p-6 lg:px-8">
          <div className="flex lg:flex-1">
            <Link to="/" className="-m-1.5 p-1.5 flex items-center gap-10">
              <img 
                src="https://static.readdy.ai/image/60cfa5760427af1c3d73f4665d1a36c9/e002dead815e5e3725a361aa0576a674.png" 
                alt="QBOVerifier Logo" 
                className="h-12 w-auto object-contain"
              />
              <span className="text-2xl font-bold" style={{ fontFamily: 'Calibri, sans-serif', color: '#29B3E6' }}>
                QBOVerifier
              </span>
            </Link>
          </div>
          
          <div className="flex lg:hidden">
            <button
              type="button"
              className="-m-2.5 inline-flex items-center justify-center rounded-md p-2.5 text-gray-700"
              onClick={() => setIsMenuOpen(!isMenuOpen)}
            >
              <i className="ri-menu-line text-xl"></i>
            </button>
          </div>
          
          <div className="hidden lg:flex lg:gap-x-12">
            {navigationBefore.map(renderNavLink)}
            
            {/* Pricing Dropdown */}
            <div 
              ref={pricingRef}
              className="relative"
              onMouseEnter={() => setIsPricingOpen(true)}
              onMouseLeave={() => setIsPricingOpen(false)}
            >
              <button
                className="text-sm font-semibold leading-6 transition-colors flex items-center gap-1"
                style={{ color: isPricingActive ? '#29B3E6' : '#111827' }}
                onClick={() => setIsPricingOpen(!isPricingOpen)}
                onMouseEnter={(e) => (e.currentTarget.style.color = '#29B3E6')}
                onMouseLeave={(e) => {
                  if (!isPricingActive) {
                    e.currentTarget.style.color = '#111827';
                  }
                }}
              >
                Pricing
                <i className={`ri-arrow-down-s-line transition-transform ${isPricingOpen ? 'rotate-180' : ''}`}></i>
              </button>
              
              {isPricingOpen && (
                <div className="absolute top-full left-0 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-2">
                  {pricingLinks.map((item) => (
                    <Link
                      key={item.name}
                      to={item.href}
                      className={`block px-4 py-2 text-sm transition-colors ${
                        location.pathname === item.href ? 'font-semibold' : 'text-gray-700'
                      }`}
                      style={location.pathname === item.href ? { color: '#29B3E6' } : {}}
                      onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#f9fafb')}
                      onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = '')}
                    >
                      {item.name}
                    </Link>
                  ))}
                </div>
              )}
            </div>

            {navigationAfter.map(renderNavLink)}
						
						{/* Affiliates Dropdown */}
            <div 
						  ref={affiliateRef}
              className="relative"
              onMouseEnter={() => setIsAffiliateOpen(true)}
              onMouseLeave={() => setIsAffiliateOpen(false)}
            >
              <button
                className="text-sm font-semibold leading-6 transition-colors flex items-center gap-1"
																																			 
									 
                style={{ color: isAffiliateActive ? '#29B3E6' : '#111827' }}
                onClick={() => setIsAffiliateOpen(!isAffiliateOpen)}
                onMouseEnter={(e) => (e.currentTarget.style.color = '#29B3E6')}
                onMouseLeave={(e) => {
                  if (!isAffiliateActive) {
                    e.currentTarget.style.color = '#111827';
                  }
                }}
              >
                Affiliates
                <i className={`ri-arrow-down-s-line transition-transform ${isAffiliateOpen ? 'rotate-180' : ''}`}></i>
              </button>
              
              {isAffiliateOpen && (
                <div className="absolute top-full left-0 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-2" style={{ marginTop: '0px' }}>
                  {affiliateLinks.map((item) => (
                    <Link
                      key={item.name}
                      to={item.href}
                      className={`block px-4 py-2 text-sm hover:bg-gray-50 transition-colors ${
                        location.pathname === item.href ? 'font-semibold' : 'text-gray-700'
                      }`}
                      style={location.pathname === item.href ? { color: '#29B3E6' } : {}}
                    >
                      {item.name}
                    </Link>
                  ))}
                </div>
              )}
            </div>

            {/* Login Link */}
            <Link
              to="/login"
              className="text-sm font-semibold leading-6 transition-colors flex items-center gap-2"
																																									
								 
              style={{ color: location.pathname === '/login' ? '#29B3E6' : '#111827' }}
              onMouseEnter={(e) => (e.currentTarget.style.color = '#29B3E6')}
              onMouseLeave={(e) => {
                if (location.pathname !== '/login') {
                  e.currentTarget.style.color = '#111827';
                }
              }}
            >
              <i className="ri-user-line"></i>
              Login
            </Link>
          </div>
          
          <div className="hidden lg:flex lg:flex-1 lg:justify-end">
            {/* Temporarily hidden - Install Extension button
            <button 
              className="text-white px-6 py-2 rounded-lg font-semibold transition-colors whitespace-nowrap"
              style={{ backgroundColor: '#29B3E6' }}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#1E9BD1'}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#29B3E6'}
              onClick={openExtensionModal}
            >
              Install Extension
            </button>
            */}
          </div>
        </nav>
        
        {/* Mobile menu */}
        {isMenuOpen && (
          <div className="lg:hidden">
            <div className="space-y-2 px-6 pb-3 pt-2">
              {navigationBefore.map((item) => (
                <Link
                  key={item.name}
                  to={item.href}
                  className="block rounded-lg px-3 py-2 text-base font-semibold leading-7 hover:bg-gray-50 text-gray-900"
                  style={location.pathname === item.href ? { color: '#29B3E6' } : {}}
                  onClick={() => setIsMenuOpen(false)}
                >
                  {item.name}
                </Link>
              ))}
              
              {/* Mobile Pricing Dropdown */}
              <div>
                <button
                  className="w-full text-left rounded-lg px-3 py-2 text-base font-semibold leading-7 hover:bg-gray-50 flex items-center justify-between text-gray-900"
                  style={isPricingActive ? { color: '#29B3E6' } : {}}
                  onClick={() => setIsPricingOpen(!isPricingOpen)}
                >
                  Pricing
                  <i className={`ri-arrow-down-s-line transition-transform ${isPricingOpen ? 'rotate-180' : ''}`}></i>
                </button>
                
                {isPricingOpen && (
                  <div className="ml-4 space-y-1">
                    {pricingLinks.map((item) => (
                      <Link
                        key={item.name}
                        to={item.href}
                        className={`block rounded-lg px-3 py-2 text-sm leading-6 hover:bg-gray-50 ${
                          location.pathname === item.href ? 'font-semibold' : 'text-gray-700'
                        }`}
                        style={location.pathname === item.href ? { color: '#29B3E6' } : {}}
                        onClick={() => {
                          setIsMenuOpen(false);
                          setIsPricingOpen(false);
                        }}
                      >
                        {item.name}
                      </Link>
                    ))}
                  </div>
                )}
              </div>

              {navigationAfter.map((item) => (
                <Link
                  key={item.name}
                  to={item.href}
                  className="block rounded-lg px-3 py-2 text-base font-semibold leading-7 hover:bg-gray-50 text-gray-900"
                  style={location.pathname === item.href ? { color: '#29B3E6' } : {}}
									onClick={() => setIsMenuOpen(false)}
                >
                  {item.name}
                </Link>
              ))}
              
              {/* Mobile Affiliates */}
              <div>
                <button
                 className="w-full text-left rounded-lg px-3 py-2 text-base font-semibold leading-7 hover:bg-gray-50 flex items-center justify-between text-gray-900"
								 
                  style={isAffiliateActive ? { color: '#29B3E6' } : {}}
                  onClick={() => setIsAffiliateOpen(!isAffiliateOpen)}
                >
                  Affiliates
                  <i className={`ri-arrow-down-s-line transition-transform ${isAffiliateOpen ? 'rotate-180' : ''}`}></i>
                </button>
                
                {isAffiliateOpen && (
                  <div className="ml-4 space-y-1">
                    {affiliateLinks.map((item) => (
                      <Link
                        key={item.name}
                        to={item.href}
                        className={`block rounded-lg px-3 py-2 text-sm leading-6 hover:bg-gray-50 ${
                          location.pathname === item.href ? 'font-semibold' : 'text-gray-700'
                        }`}
                        style={location.pathname === item.href ? { color: '#29B3E6' } : {}}
                        onClick={() => {
                          setIsMenuOpen(false);
                          setIsAffiliateOpen(false);
                        }}
                      >
                        {item.name}
                      </Link>
                    ))}
                  </div>
                )}
              </div>

              {/* Mobile Login Link */}
              <Link
                to="/login"
                className="block rounded-lg px-3 py-2 text-base font-semibold leading-7 hover:bg-gray-50 flex items-center gap-2 text-gray-900"
								
                style={location.pathname === '/login' ? { color: '#29B3E6' } : {}}
                onClick={() => setIsMenuOpen(false)}
              >
                <i className="ri-user-line"></i>
                Login
              </Link>
              
              <button 
                className="w-full text-white px-6 py-2 rounded-lg font-semibold transition-colors mt-4 whitespace-nowrap"
                style={{ backgroundColor: '#29B3E6' }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#1E9BD1'}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#29B3E6'}
                onClick={openExtensionModal}
              >
                Install Extension
              </button>
            </div>
          </div>
        )}
      </header>

      {/* Extension Selection Modal */}
      <ExtensionSelectionModal 
        isOpen={isExtensionModalOpen}
        onClose={closeExtensionModal}
      />
    </>
  );
}

